import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { Tooltip } from "@mui/material";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBoxOpen,
  faTriangleExclamation,
  faChartLine,
  faCircleInfo,
} from "@fortawesome/free-solid-svg-icons";
import Card from "../ui/Card";
import { fmtMoney, fmtPct } from "../../utils/inventory/receiverMfcaUtils";
import "../../style/mfca-dashboard.css";

export const InfoTip = ({ text, placement = "top" }) => (
  <Tooltip title={text} arrow placement={placement}>
    <span className="mfca-infotip" role="img" aria-label="info">
      <FontAwesomeIcon icon={faCircleInfo} />
    </span>
  </Tooltip>
);

InfoTip.propTypes = {
  text: PropTypes.string.isRequired,
  placement: PropTypes.string,
};

const ReceiverMFCASummary = ({ counters, loading = false, darkMode = false }) => {
  const { t } = useTranslation();

  const valueReclaimed = counters?.valueReclaimed || 0;
  const notUtilized = counters?.notUtilized || { total: 0, disposalLoss: 0, qualityIssue: 0 };
  const utilizationRate = counters?.utilizationRate || 0;

  const hasData = valueReclaimed > 0 || notUtilized.total > 0 || utilizationRate > 0;
  if (!hasData && !loading) return null;

  const cards = [
    {
      icon: faBoxOpen,
      value: fmtMoney(valueReclaimed),
      title: t("Value Reclaimed"),
      description: t("vs. virgin-market baseline"),
      colorClass: "counter-green",
      tip: t("Estimated savings vs. buying these materials new. Based on reference prices where available, falling back to listing cost or your acquisition cost. This is an estimate, not accounting-grade."),
    },
    {
      icon: faTriangleExclamation,
      value: fmtMoney(notUtilized.total),
      title: t("Not Utilized"),
      description: null,
      colorClass: "counter-amber",
      tip: t("Dollar value of rejected material in this period, split by what happened to it: disposal loss (you disposed of it locally) or quality issue (returned to donor or credited)."),
      breakdown: [
        {
          key: "disposal",
          value: fmtMoney(notUtilized.disposalLoss),
          label: t("disposal loss (disposed by you)"),
        },
        {
          key: "quality",
          value: fmtMoney(notUtilized.qualityIssue),
          label: t("quality issue (returned / credited by donor)"),
        },
      ],
    },
    {
      icon: faChartLine,
      value: fmtPct(utilizationRate),
      title: t("Utilization Rate"),
      description: t("accepted ÷ delivered"),
      colorClass: "counter-blue",
      tip: t("Share of delivered material you accepted. Read this alongside Donor Quality Insights below — a single number tells you what happened, the donor breakdown tells you where it came from."),
    },
  ];

  return (
    <div className="mfca-summary-wrap">
      <p className="mfca-disclosure">
        {t("Material Flow Analysis (mass) + Material Flow Cost Accounting (value), ISO 14051-aligned. Estimates, not accounting-grade.")}
      </p>
      <div className="counter-cards-container">
        {cards.map((card) => (
          <Card
            key={card.title}
            icon={card.icon}
            value={card.value}
            title={
              <span style={{ display: "flex", alignItems: "center" }}>
                {card.title}
                <InfoTip text={card.tip} />
              </span>
            }
            description={card.description}
            variant="counter"
            colorClass={card.colorClass}
            darkMode={darkMode}
            isLoading={loading}
          >
            {card.breakdown && (
              <ul className="mfca-breakdown">
                {card.breakdown.map((b) => (
                  <li key={b.key}>
                    <span className="mfca-breakdown-value">{b.value}</span>
                    <span className="mfca-breakdown-label">{b.label}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        ))}
      </div>
    </div>
  );
};

ReceiverMFCASummary.propTypes = {
  counters: PropTypes.shape({
    valueReclaimed: PropTypes.number,
    notUtilized: PropTypes.shape({
      total: PropTypes.number,
      disposalLoss: PropTypes.number,
      qualityIssue: PropTypes.number,
    }),
    utilizationRate: PropTypes.number,
  }),
  loading: PropTypes.bool,
  darkMode: PropTypes.bool,
};

export default ReceiverMFCASummary;
